import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { format } from 'date-fns';
import { ca, es } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { 
  Cat, CalendarIcon, User, PenTool, 
  Check, ChevronRight, ChevronLeft, Eraser, FileText, LogOut, 
  Stethoscope, HeartHandshake, ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

// Sub-components FORA del component principal (evita recrear en cada render → perd focus)
const InputField = ({ label, value, onChange, placeholder, testId, type = "text", icon: Icon, optional }) => (
  <div>
    <Label className="text-sm font-medium text-stone-700 flex items-center gap-2">
      {Icon && <Icon size={14} />} {label}
      {optional && <span className="text-xs font-normal text-stone-400">(opcional)</span>}
    </Label>
    <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-2 rounded-xl border-stone-200 bg-stone-50" data-testid={testId} />
  </div>
);

const DatePickerField = ({ label, value, onSelect, testId, placeholder, locale, optional }) => (
  <div>
    <Label className="text-sm font-medium text-stone-700 flex items-center gap-2 mb-2">
      <CalendarIcon size={14} /> {label}
      {optional && <span className="text-xs font-normal text-stone-400">(opcional)</span>}
    </Label>
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className={cn("w-full justify-start text-left font-normal rounded-xl border-stone-200 bg-stone-50 hover:bg-stone-100", !value && "text-stone-400")} data-testid={testId}>
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? format(value, "PPP", { locale }) : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={value} onSelect={onSelect} initialFocus />
      </PopoverContent>
    </Popover>
  </div>
);

export default function AdoptionFormPage() {
  const { logout } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const signatureRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(1);
  
  const locale = language === 'es' ? es : ca;
  
  const steps = [
    { id: 1, title: t('step_personal'), icon: User },
    { id: 2, title: t('step_cat'), icon: Cat },
    { id: 3, title: t('step_vet'), icon: Stethoscope },
    { id: 4, title: t('step_dates'), icon: CalendarIcon },
    { id: 5, title: t('step_signature'), icon: PenTool },
  ];
  
  const [formData, setFormData] = useState({
    // Adoptant
    nombre: '', apellidos: '', dni: '', telefono: '', direccion: '', 
    codigo_postal: '', poblacion: '', adoptante_email: '',
    // Gat
    procedencia: '',
    nombre_gato: '', microchip: '',
    especie: 'gat', sexe: '', edat_gat: '', data_naixement: null,
    raca: 'europeu', raca_altre: '', color: '', altres_senyes: '',
    pel: 'curt', esterilitzat: 'no', test_felv: '',
    // Veterinari
    veterinari_nom: '', veterinari_collegiat: '', veterinari_cartilla: '', 
    veterinari_doc: '', veterinari_aiac_data: null,
    // Dates
    fecha_adopcion: null, fecha_vacunacion: null, fecha_vacunacion2: null, fecha_esterilizacion: null,
    // Donatiu
    donatiu: '',
    // Consentiments (SÍ/NO)
    consent_photos: '', consent_info: '',
    // Signatura
    firma: '', accept_data: false, accept_terms: false
  });

  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleLogout = () => { logout(); navigate('/'); };
  const clearSignature = () => { signatureRef.current?.clear(); updateField('firma', ''); };
  const saveSignature = () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      updateField('firma', signatureRef.current.toDataURL('image/png'));
      toast.success(t('form_signature_saved_toast'));
    } else {
      toast.error(t('form_signature_error'));
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.nombre || !formData.apellidos || !formData.dni || !formData.telefono || !formData.direccion || !formData.adoptante_email) {
          toast.error(t('form_validation_error')); return false;
        }
        return true;
      case 2:
        if (!formData.nombre_gato || !formData.microchip) {
          toast.error(t('form_validation_error')); return false;
        }
        if (!/^\d{15}$/.test(formData.microchip)) {
          toast.error(t('form_microchip_error')); return false;
        }
        return true;
      case 3:
        if (!formData.veterinari_nom || !formData.veterinari_collegiat) {
          toast.error(t('form_validation_error')); return false;
        }
        return true;
      case 4:
        if (!formData.fecha_adopcion) {
          toast.error(t('form_validation_error')); return false;
        }
        return true;
      case 5:
        if (!formData.firma) { toast.error(t('form_signature_error')); return false; }
        if (!formData.consent_photos || !formData.consent_info) { toast.error(t('form_consents_error')); return false; }
        if (!formData.accept_data || !formData.accept_terms) { toast.error(t('form_terms_error')); return false; }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => { if (validateStep(currentStep)) setCurrentStep(prev => Math.min(prev + 1, 5)); };
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const handleGenerate = () => {
    if (!validateStep(5)) return;
    const dataForPdf = {
      ...formData,
      fecha_adopcion: formData.fecha_adopcion ? format(formData.fecha_adopcion, 'dd/MM/yyyy') : '',
      fecha_vacunacion: formData.fecha_vacunacion ? format(formData.fecha_vacunacion, 'dd/MM/yyyy') : '',
      fecha_vacunacion2: formData.fecha_vacunacion2 ? format(formData.fecha_vacunacion2, 'dd/MM/yyyy') : '',
      fecha_esterilizacion: formData.fecha_esterilizacion ? format(formData.fecha_esterilizacion, 'dd/MM/yyyy') : '',
      veterinari_aiac_data: formData.veterinari_aiac_data ? format(formData.veterinari_aiac_data, 'dd/MM/yyyy') : '',
      data_naixement: formData.data_naixement ? format(formData.data_naixement, 'dd/MM/yyyy') : ''
    };
    navigate('/success', { state: { formData: dataForPdf } });
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-5 animate-fade-in">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-4">{t('form_adopter_data')}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField label={t('form_name')} value={formData.nombre} onChange={(v) => updateField('nombre', v)} placeholder={t('form_name_ph')} testId="input-nombre" />
              <InputField label={t('form_surname')} value={formData.apellidos} onChange={(v) => updateField('apellidos', v)} placeholder={t('form_surname_ph')} testId="input-apellidos" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField label={t('form_dni')} value={formData.dni} onChange={(v) => updateField('dni', v)} placeholder={t('form_dni_ph')} testId="input-dni" />
              <InputField label={t('form_phone')} value={formData.telefono} onChange={(v) => updateField('telefono', v)} placeholder={t('form_phone_ph')} testId="input-telefono" />
            </div>
            <InputField label={t('form_address')} value={formData.direccion} onChange={(v) => updateField('direccion', v)} placeholder={t('form_address_ph')} testId="input-direccion" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField optional label={t('form_postal_code')} value={formData.codigo_postal} onChange={(v) => updateField('codigo_postal', v)} placeholder={t('form_postal_code_ph')} testId="input-cp" />
              <InputField optional label={t('form_city')} value={formData.poblacion} onChange={(v) => updateField('poblacion', v)} placeholder={t('form_city_ph')} testId="input-city" />
            </div>
            <InputField label={t('form_email')} value={formData.adoptante_email} onChange={(v) => updateField('adoptante_email', v)} placeholder={t('form_email_ph')} testId="input-email" type="email" />
          </div>
        );
        
      case 2:
        return (
          <div className="space-y-5 animate-fade-in">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-4">{t('form_cat_data')}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField optional label={t('form_procedencia')} value={formData.procedencia} onChange={(v) => updateField('procedencia', v)} placeholder={t('form_procedencia_ph')} testId="input-procedencia" />
              <InputField label={t('form_cat_name')} value={formData.nombre_gato} onChange={(v) => updateField('nombre_gato', v)} placeholder={t('form_cat_name_ph')} testId="input-nombre-gato" />
            </div>
            
            {/* Sexe */}
            <div>
              <Label className="text-sm font-medium text-stone-700 mb-2 block">{t('form_sex')}</Label>
              <RadioGroup value={formData.sexe} onValueChange={(v) => updateField('sexe', v)} className="flex gap-6">
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="mascle" id="mascle" />
                  <Label htmlFor="mascle" className="cursor-pointer">{t('form_sex_male')}</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="femella" id="femella" />
                  <Label htmlFor="femella" className="cursor-pointer">{t('form_sex_female')}</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField optional label={t('form_cat_age')} value={formData.edat_gat} onChange={(v) => updateField('edat_gat', v)} placeholder={t('form_cat_age_ph')} testId="input-edat" />
              <DatePickerField optional locale={locale} placeholder={t("form_date_placeholder")} label={t('form_cat_birth')} value={formData.data_naixement} onSelect={(d) => updateField('data_naixement', d)} testId="date-naixement" />
            </div>

            {/* Raça */}
            <div>
              <Label className="text-sm font-medium text-stone-700 mb-2 block">{t('form_cat_breed')}</Label>
              <RadioGroup value={formData.raca} onValueChange={(v) => updateField('raca', v)} className="flex gap-6">
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="europeu" id="europeu" />
                  <Label htmlFor="europeu" className="cursor-pointer">{t('form_cat_breed_european')}</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="altre" id="altre" />
                  <Label htmlFor="altre" className="cursor-pointer">{t('form_cat_breed_other')}</Label>
                </div>
              </RadioGroup>
              {formData.raca === 'altre' && (
                <Input value={formData.raca_altre} onChange={(e) => updateField('raca_altre', e.target.value)} placeholder={t('form_cat_breed_other')} className="mt-2 rounded-xl border-stone-200 bg-stone-50" />
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField optional label={t('form_cat_color')} value={formData.color} onChange={(v) => updateField('color', v)} placeholder={t('form_cat_color_ph')} testId="input-color" />
              <InputField optional label={t('form_cat_marks')} value={formData.altres_senyes} onChange={(v) => updateField('altres_senyes', v)} placeholder={t('form_cat_marks_ph')} testId="input-senyes" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Pèl */}
              <div>
                <Label className="text-sm font-medium text-stone-700 mb-2 block">{t('form_cat_hair')}</Label>
                <RadioGroup value={formData.pel} onValueChange={(v) => updateField('pel', v)} className="flex gap-6">
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="curt" id="curt" />
                    <Label htmlFor="curt" className="cursor-pointer">{t('form_cat_hair_short')}</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="llarg" id="llarg" />
                    <Label htmlFor="llarg" className="cursor-pointer">{t('form_cat_hair_long')}</Label>
                  </div>
                </RadioGroup>
              </div>
              {/* Esterilitzat */}
              <div>
                <Label className="text-sm font-medium text-stone-700 mb-2 block">{t('form_sterilized')}</Label>
                <RadioGroup value={formData.esterilitzat} onValueChange={(v) => updateField('esterilitzat', v)} className="flex gap-6">
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="si" id="est-si" />
                    <Label htmlFor="est-si" className="cursor-pointer">{t('form_yes')}</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="no" id="est-no" />
                    <Label htmlFor="est-no" className="cursor-pointer">{t('form_no')}</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>

            {/* Microxip amb validació de 15 dígits */}
            <div>
              <Label className="text-sm font-medium text-stone-700 flex items-center gap-2">
                {t('form_microchip')}
              </Label>
              <Input
                type="text"
                inputMode="numeric"
                value={formData.microchip}
                onChange={(e) => updateField('microchip', e.target.value.replace(/\D/g, '').slice(0, 15))}
                placeholder={t('form_microchip_ph')}
                maxLength={15}
                className={cn(
                  "mt-2 rounded-xl border-stone-200 bg-stone-50",
                  formData.microchip && !/^\d{15}$/.test(formData.microchip) && "border-red-400 focus-visible:ring-red-400"
                )}
                data-testid="input-microchip"
              />
              <p className={cn(
                "text-xs mt-1",
                !formData.microchip ? "text-stone-400" :
                /^\d{15}$/.test(formData.microchip) ? "text-emerald-600" : "text-red-500"
              )}>
                {formData.microchip
                  ? (/^\d{15}$/.test(formData.microchip)
                      ? `✓ ${t('form_microchip_ok')}`
                      : `${formData.microchip.length}/15 ${t('form_microchip_hint')}`)
                  : t('form_microchip_hint_empty')}
              </p>
            </div>
            <InputField optional label={t('form_test_felv')} value={formData.test_felv} onChange={(v) => updateField('test_felv', v)} placeholder={t('form_test_felv_ph')} testId="input-test-felv" />
          </div>
        );
        
      case 3:
        return (
          <div className="space-y-5 animate-fade-in">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-4">{t('form_vet_data')}</p>
            <InputField label={t('form_vet_name')} value={formData.veterinari_nom} onChange={(v) => updateField('veterinari_nom', v)} placeholder={t('form_vet_name_ph')} testId="input-vet-nom" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField label={t('form_vet_collegiate')} value={formData.veterinari_collegiat} onChange={(v) => updateField('veterinari_collegiat', v)} placeholder={t('form_vet_collegiate_ph')} testId="input-vet-collegiat" />
              <InputField optional label={t('form_vet_card_type')} value={formData.veterinari_cartilla} onChange={(v) => updateField('veterinari_cartilla', v)} placeholder={t('form_vet_card_type_ph')} testId="input-vet-cartilla" />
            </div>
            <InputField optional label={t('form_vet_doc_id')} value={formData.veterinari_doc} onChange={(v) => updateField('veterinari_doc', v)} placeholder={t('form_vet_doc_id_ph')} testId="input-vet-doc" />
            <DatePickerField optional locale={locale} placeholder={t("form_date_placeholder")} label={t('form_vet_aiac_date')} value={formData.veterinari_aiac_data} onSelect={(d) => updateField('veterinari_aiac_data', d)} testId="date-vet-aiac" />
            
            <div className="bg-blue-50 rounded-2xl p-6 border border-blue-100">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-100 rounded-xl"><Stethoscope className="h-6 w-6 text-blue-600" /></div>
                <div>
                  <h4 className="font-semibold text-stone-900 mb-1">{t('form_vet_info_title')}</h4>
                  <p className="text-sm text-stone-600">{t('form_vet_info_desc')}</p>
                </div>
              </div>
            </div>
          </div>
        );
        
      case 4:
        return (
          <div className="space-y-5 animate-fade-in">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-4">{t('form_dates')}</p>
            <DatePickerField locale={locale} placeholder={t("form_date_placeholder")} label={t('form_date_adoption')} value={formData.fecha_adopcion} onSelect={(d) => updateField('fecha_adopcion', d)} testId="date-adopcion" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DatePickerField optional locale={locale} placeholder={t("form_date_placeholder")} label={t('form_date_vaccination')} value={formData.fecha_vacunacion} onSelect={(d) => updateField('fecha_vacunacion', d)} testId="date-vacunacion" />
              <DatePickerField optional locale={locale} placeholder={t("form_date_placeholder")} label={t('form_date_vaccination2')} value={formData.fecha_vacunacion2} onSelect={(d) => updateField('fecha_vacunacion2', d)} testId="date-vacunacion2" />
            </div>
            <DatePickerField optional locale={locale} placeholder={t("form_date_placeholder")} label={t('form_date_sterilization')} value={formData.fecha_esterilizacion} onSelect={(d) => updateField('fecha_esterilizacion', d)} testId="date-esterilizacion" />
            
            {/* Donatiu */}
            <div className="pt-4 border-t border-stone-100">
              <InputField optional label={t('form_donation')} value={formData.donatiu} onChange={(v) => updateField('donatiu', v)} placeholder={t('form_donation_ph')} testId="input-donatiu" icon={HeartHandshake} />
              <p className="text-xs text-stone-500 mt-2">{t('form_donation_help')}</p>
            </div>
          </div>
        );
        
      case 5:
        return (
          <div className="space-y-5 animate-fade-in">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-4">{t('form_signature')}</p>
            
            <div className="space-y-3">
              <div className="rounded-xl border-2 border-dashed border-stone-300 bg-stone-50 overflow-hidden">
                <SignatureCanvas ref={signatureRef} canvasProps={{ className: 'signature-canvas w-full h-48', style: { width: '100%', height: '200px' }, 'data-testid': 'signature-canvas' }} backgroundColor="rgb(250, 250, 249)" />
              </div>
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={clearSignature} className="flex-1 rounded-xl" data-testid="clear-signature-btn">
                  <Eraser size={16} className="mr-2" />{t('form_clear')}
                </Button>
                <Button type="button" onClick={saveSignature} className="flex-1 rounded-xl bg-emerald-600 hover:bg-emerald-700" data-testid="save-signature-btn">
                  <Check size={16} className="mr-2" />{t('form_save_signature')}
                </Button>
              </div>
              {formData.firma && (
                <div className="flex items-center gap-2 text-emerald-600 text-sm">
                  <Check size={16} />{t('form_signature_saved')}
                </div>
              )}
            </div>
            
            {/* Consentiments SÍ/NO */}
            <div className="space-y-4 pt-4 border-t border-stone-100">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">{t('form_consents_title')}</p>

              <div>
                <p className="text-sm text-stone-700 mb-2">{t('form_consent_photos')}</p>
                <RadioGroup value={formData.consent_photos} onValueChange={(v) => updateField('consent_photos', v)} className="flex gap-6">
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="si" id="consent-photos-si" data-testid="consent-photos-si" />
                    <Label htmlFor="consent-photos-si" className="cursor-pointer">{t('form_yes')}</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="no" id="consent-photos-no" data-testid="consent-photos-no" />
                    <Label htmlFor="consent-photos-no" className="cursor-pointer">{t('form_no')}</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <p className="text-sm text-stone-700 mb-2">{t('form_consent_info')}</p>
                <RadioGroup value={formData.consent_info} onValueChange={(v) => updateField('consent_info', v)} className="flex gap-6">
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="si" id="consent-info-si" data-testid="consent-info-si" />
                    <Label htmlFor="consent-info-si" className="cursor-pointer">{t('form_yes')}</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="no" id="consent-info-no" data-testid="consent-info-no" />
                    <Label htmlFor="consent-info-no" className="cursor-pointer">{t('form_no')}</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>

            {/* Checkboxes acceptació */}
            <div className="space-y-3 pt-4 border-t border-stone-100">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={formData.accept_data} onChange={(e) => updateField('accept_data', e.target.checked)} className="mt-1 w-4 h-4 rounded border-stone-300 text-orange-600 focus:ring-orange-500" data-testid="accept-data-checkbox" />
                <span className="text-sm text-stone-700">{t('form_accept_data')}</span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={formData.accept_terms} onChange={(e) => updateField('accept_terms', e.target.checked)} className="mt-1 w-4 h-4 rounded border-stone-300 text-orange-600 focus:ring-orange-500" data-testid="accept-terms-checkbox" />
                <span className="text-sm text-stone-700">{t('form_accept_terms')}</span>
              </label>
            </div>
            
            <div className="bg-stone-100 rounded-2xl p-6 mt-6">
              <h4 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
                <FileText size={16} />{t('form_summary')}
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-stone-500">{t('form_summary_adopter')}</span><p className="font-medium">{formData.nombre} {formData.apellidos}</p></div>
                <div><span className="text-stone-500">{t('form_summary_dni')}</span><p className="font-medium">{formData.dni}</p></div>
                <div><span className="text-stone-500">{t('form_summary_cat')}</span><p className="font-medium">{formData.nombre_gato}</p></div>
                <div><span className="text-stone-500">{t('form_summary_date')}</span><p className="font-medium">{formData.fecha_adopcion ? format(formData.fecha_adopcion, "dd/MM/yyyy") : '-'}</p></div>
              </div>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="adoption-form-page">
      <div className="w-full lg:w-3/5 flex flex-col bg-stone-50">
        <header className="px-8 py-6 border-b border-stone-200 bg-white flex items-center justify-between">
          <img src="/logo.jpg" alt="Gats del Carrer" className="h-12 w-auto" data-testid="form-logo" />
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/control')} className="text-stone-600 hover:text-stone-900" data-testid="go-control-header-btn">
              <ClipboardList size={16} className="mr-2" />{t('control_page_title')}
            </Button>
            <LanguageSelector variant="compact" />
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-stone-500 hover:text-stone-700" data-testid="logout-btn">
              <LogOut size={16} className="mr-2" />{t('logout')}
            </Button>
          </div>
        </header>

        <div className="px-4 py-6 bg-white border-b border-stone-200 overflow-x-auto">
          <div className="flex items-center justify-between min-w-max px-4 mx-auto">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              return (
                <div key={step.id} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div className={cn("flex items-center justify-center w-10 h-10 rounded-full transition-all", isCompleted && "bg-emerald-600 text-white", isActive && "bg-orange-600 text-white", !isActive && !isCompleted && "bg-stone-200 text-stone-500")}>
                      {isCompleted ? <Check size={18} /> : <Icon size={18} />}
                    </div>
                    <span className={cn("text-xs mt-2 hidden sm:block whitespace-nowrap", isActive ? "text-orange-600 font-medium" : "text-stone-500")}>{step.title}</span>
                  </div>
                  {index < steps.length - 1 && <div className={cn("w-8 sm:w-12 h-0.5 mx-2", isCompleted ? "bg-emerald-600" : "bg-stone-200")} />}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex-1 px-8 py-8 overflow-y-auto">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-3xl border border-stone-100 p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-stone-900 mb-6">{steps[currentStep - 1].title}</h2>
              {renderStepContent()}
              <div className="flex justify-between mt-8 pt-6 border-t border-stone-100">
                <Button type="button" variant="outline" onClick={prevStep} disabled={currentStep === 1} className="rounded-full px-6" data-testid="prev-step-btn">
                  <ChevronLeft size={16} className="mr-2" />{t('form_prev')}
                </Button>
                {currentStep < 5 ? (
                  <Button type="button" onClick={nextStep} className="rounded-full px-6 bg-orange-600 hover:bg-orange-700" data-testid="next-step-btn">
                    {t('form_next')}<ChevronRight size={16} className="ml-2" />
                  </Button>
                ) : (
                  <Button type="button" onClick={handleGenerate} disabled={!formData.firma} className="rounded-full px-6 bg-emerald-600 hover:bg-emerald-700" data-testid="submit-form-btn">
                    <Check size={16} className="mr-2" />{t('form_generate')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden lg:block lg:w-2/5 form-background relative">
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/70 via-stone-900/20 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="text-3xl font-bold mb-3">{t('login_slogan_title')}</h2>
          <p className="text-stone-200 text-lg">{t('login_slogan_description')}</p>
        </div>
      </div>
    </div>
  );
}
